import React from "react";
import { Box } from "@mui/material";
import PrecisionManufacturingOutlined from "@mui/icons-material/PrecisionManufacturingOutlined";
import { dt } from "../../../shared/themes/redesignTokens";
import { Machine } from "../../models/machinesModels";
import {
  getMachineDisplayName,
  getBrandDisplayName,
  getMachineName,
} from "../../../shared/utils/machineUtils";
export interface MachineContextHeaderProps {
  machine?: Machine;
}

/** Resolve the best available image for a public machine. */
export const getPublicMachineImageUrl = (machine?: Machine | null): string | null => {
  if (!machine) return null;
  // image.content is either a base64 data-URI or an absolute URL served by the API
  if (machine.image && machine.image.content) return machine.image.content;
  // Fallback to catalog image URL resolved by the backend
  if (machine.catalogImageUrl) return machine.catalogImageUrl;
  return null;
};

const getHealthColor = (openReports: number): string => {
  if (openReports === 0) return "#27AE60"; // green
  if (openReports <= 2) return "#F39C12"; // yellow/amber
  return "#C0392B"; // red
};

const MachineContextHeader: React.FC<MachineContextHeaderProps> = ({
  machine,
}) => {
  if (!machine) return null;

  const openReports = machine.openServiceReportsCount || 0;
  const healthColor = getHealthColor(openReports);
  const brandName = getBrandDisplayName(machine);
  const typeName = getMachineName(machine);
  const brandType = [brandName, typeName].filter(Boolean).join(" \u00B7 ");
  const imageUrl = getPublicMachineImageUrl(machine);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "16px",
        backgroundColor: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
      }}
    >
      {/* Machine image */}
      <Box
        sx={{
          width: "72px",
          height: "72px",
          flexShrink: 0,
          borderRadius: dt.radiusSm,
          border: `1px solid ${dt.cardBorder}`,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F8FAFC",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={getMachineDisplayName(machine)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <PrecisionManufacturingOutlined
            sx={{ fontSize: "32px", color: dt.textMuted, opacity: 0.5 }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            fontFamily: dt.font,
            color: dt.textPrimary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getMachineDisplayName(machine)}
        </Box>

        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 400,
            fontFamily: dt.font,
            color: dt.textSecondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mt: 0.25,
          }}
        >
          {brandType}
        </Box>

        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 400,
            fontFamily: dt.fontMono,
            color: dt.textMuted,
            mt: 0.25,
          }}
        >
          {machine.serialNumber ? `S/N ${machine.serialNumber}` : ""}
        </Box>
      </Box>

      {/* Health indicator dot - no count number */}
      <Box
        sx={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor: healthColor,
          flexShrink: 0,
        }}
      />
    </Box>
  );
};

export default MachineContextHeader;
